/**
 * Primary navigation icons — the app's spine (desktop rail in CollapsiblePanel, mobile
 * pills in MobileBottomNav). These are the only icons with a real DUOTONE treatment:
 * an outline silhouette at rest and a filled silhouette when active.
 *
 * Active-state contract (MERIDIAN): the fill swap is a *supplement*, never the sole cue.
 * It is layered on the latitude tick + icon recolor to --nav-tick-color + label weight
 * 500→600 + aria-current (docs/design/MERIDIAN.md:1585, :1425). The desktop rail expresses
 * active purely in CSS and renders these single-silhouette (outline); the mobile nav swaps
 * outline→filled per tab. Each concept therefore exposes ONE component with a `filled` prop
 * (and a Filled convenience alias) so both models are served from one silhouette.
 *
 * Filled variants use REAL fill="currentColor" on the primary shape — replacing the earlier
 * MobileBottomNav "filled" variants that were only a thicker stroke.
 *
 * Cartographic throughline: the magnifier lens is a graticule globe, Availability is a
 * plotted coverage line, Observation is an instrument aperture taking a fix, Statistics is
 * a plotted bar readout, More is the overflow row.
 */
import { Icon, type IconProps } from './Icon';

export interface DuotoneIconProps extends IconProps {
  /** Active state: render the filled silhouette. Default false (outline). */
  filled?: boolean;
}

/** Find a station. Lens = graticule globe; handle = round-cap stroke. */
export const Search = ({ filled = false, ...props }: DuotoneIconProps) => (
  <Icon {...props}>
    <circle cx="11" cy="11" r="7" {...(filled ? { fill: 'currentColor' } : {})} />
    <path d="M20 20L16 16" />
  </Icon>
);

/** Data coverage plotted against time (parameter-year heatmap page). */
export const Availability = ({ filled = false, ...props }: DuotoneIconProps) => (
  <Icon {...props}>
    <path d="M3 3V21H21" />
    <path d="M7 16L12 11L15 14L21 8" />
    {filled ? (
      <>
        <circle cx="7" cy="16" r="1.75" fill="currentColor" stroke="none" />
        <circle cx="12" cy="11" r="1.75" fill="currentColor" stroke="none" />
        <circle cx="15" cy="14" r="1.75" fill="currentColor" stroke="none" />
        <circle cx="21" cy="8" r="1.75" fill="currentColor" stroke="none" />
      </>
    ) : null}
  </Icon>
);

/** A live reading at a station — an instrument aperture taking a fix. */
export const Observation = ({ filled = false, ...props }: DuotoneIconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r={filled ? 3.5 : 3} {...(filled ? { fill: 'currentColor' } : {})} />
    <path d="M12 2V5M12 19V22M2 12H5M19 12H22" />
  </Icon>
);

/** Aggregate network figures — three rising bars. */
export const Statistics = ({ filled = false, ...props }: DuotoneIconProps) => {
  const fill = filled ? { fill: 'currentColor' } : {};
  return (
    <Icon {...props}>
      <rect x="3" y="13" width="4" height="8" rx="1" {...fill} />
      <rect x="10" y="8" width="4" height="13" rx="1" {...fill} />
      <rect x="17" y="3" width="4" height="18" rx="1" {...fill} />
    </Icon>
  );
};

/** Overflow menu (mobile 5th slot) — three dots. */
export const More = ({ filled = false, ...props }: DuotoneIconProps) => {
  const r = filled ? 2 : 1.5;
  return (
    <Icon {...props}>
      <circle cx="5" cy="12" r={r} fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r={r} fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r={r} fill="currentColor" stroke="none" />
    </Icon>
  );
};

// ── Filled convenience aliases ─────────────────────────────────────────────
// So a consumer can pass a component reference (e.g. MobileBottomNav's navItems table
// stores { Outline, Filled }) rather than threading a prop.
export const SearchFilled = (props: IconProps) => <Search filled {...props} />;
export const AvailabilityFilled = (props: IconProps) => <Availability filled {...props} />;
export const ObservationFilled = (props: IconProps) => <Observation filled {...props} />;
export const StatisticsFilled = (props: IconProps) => <Statistics filled {...props} />;
export const MoreFilled = (props: IconProps) => <More filled {...props} />;

// ── Nav sub-items (single-silhouette, line-only) ───────────────────────────
// Station-scoped and analytics sub-destinations. No duotone — the rail's CSS-active
// model (surface tint + icon recolor + weight) is the state cue for these.

/** A specific station by code — a survey-marker pin. */
export const StationPin = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </Icon>
);

/** A lat/long fix — a coordinate reticle. */
export const Coordinate = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="7" />
    <path d="M12 2V6M12 18V22M2 12H6M18 12H22" />
  </Icon>
);

/** Search within a national boundary — a meridian globe. */
export const Country = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12H21M12 3c2.5 2.4 3.9 5.6 4 9-.1 3.4-1.5 6.6-4 9-2.5-2.4-3.9-5.6-4-9 .1-3.4 1.5-6.6 4-9z" />
  </Icon>
);

/** Station metadata — "i" in a circle. (Nav destination; distinct in context from StatusInfo.) */
export const Info = (props: IconProps) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11V16" />
    <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
  </Icon>
);

/** Network health / ingestion pulse — an ECG heartbeat. */
export const Health = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 12h4l2-6 4 12 2-6h6" />
  </Icon>
);

/** Network dashboards — axis + plotted trend line. */
export const Analytics = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 3 3 5-7" />
  </Icon>
);

/** Quality control / data trust — a shield with a check. */
export const QC = (props: IconProps) => (
  <Icon {...props}>
    <path d="M12 3L19 6V11C19 15.5 16 19 12 21C8 19 5 15.5 5 11V6L12 3Z" />
    <path d="M9 12L11 14L15 10" />
  </Icon>
);
